"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { assistantApi } from "#/api/assistantApi";
import {
  AssistantChatContext,
  type AssistantUiMessage,
} from "#/context/AssistantChatContext";
import { AssistantTone, type AssistantConfig } from "#/types/assistant";

const ASSISTANT_CONFIG_KEY = ["assistant-config"];

const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const AssistantChatProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<AssistantUiMessage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [tone, setTone] = useState<AssistantTone>(AssistantTone.AUTO);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggleMaximized = useCallback(() => setIsMaximized((v) => !v), []);
  const clear = useCallback(() => setMessages([]), []);

  const handleSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) {
        return;
      }

      const userMessage: AssistantUiMessage = {
        id: newId(),
        role: "user",
        content: trimmed,
      };

      // If the user is out of their daily allowance, answer locally with a
      // bot-style message instead of spending API credits on a blocked request.
      const config = queryClient.getQueryData<AssistantConfig>(
        ASSISTANT_CONFIG_KEY
      );
      if (config && config.remaining <= 0) {
        setMessages((prev) => [
          ...prev,
          userMessage,
          {
            id: newId(),
            role: "assistant",
            content: `You've reached your daily limit of ${config.dailyLimit} messages. It resets tomorrow — please check back then.`,
          },
        ]);
        return;
      }

      const history = [...messages, userMessage].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      setMessages((prev) => [...prev, userMessage]);
      setIsSending(true);

      try {
        const response = await assistantApi.chat({ messages: history, tone });
        setMessages((prev) => [
          ...prev,
          { id: newId(), role: "assistant", content: response.data.reply },
        ]);
        // Refresh the usage counter after a successful message.
        void queryClient.invalidateQueries({ queryKey: ASSISTANT_CONFIG_KEY });
      } catch {
        // The API client already surfaces a toast; add a graceful bubble too.
        setMessages((prev) => [
          ...prev,
          {
            id: newId(),
            role: "assistant",
            content: "Sorry, something went wrong. Please try again.",
          },
        ]);
      } finally {
        setIsSending(false);
      }
    },
    [messages, tone, isSending, queryClient]
  );

  const sendMessage = useCallback(
    (text: string) => {
      void handleSend(text);
    },
    [handleSend]
  );

  return (
    <AssistantChatContext.Provider
      value={{
        messages,
        isOpen,
        isMaximized,
        isSending,
        tone,
        open,
        close,
        toggleMaximized,
        setTone,
        sendMessage,
        clear,
      }}
    >
      {children}
    </AssistantChatContext.Provider>
  );
};
